import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColDef, CellValueChangedEvent, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { NJInlineMessage, NJTooltip } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import { Size2MixAccordion, Size2MixExpandCollapseBar, Size2MixSection, useSectionsToggle } from '@shared/size2mix-shell';
import { COLUMNS, SECTION_IDS, TITLES } from './columns';
import { useEditSynthesis } from './hooks';
import type { KpiEdit, KpiRow, SectionId, SynthesisData } from './types';
import './synthesis.css';

// AG Grid 32 (Nemo) floors each flex column and gives the remainder to the last one;
// AG Grid 31 rounds instead. Reproduce the Nemo distribution so columns line up.
function fillRoundingGap(api: GridApi<KpiRow>, viewportWidth: number) {
    const columns = api.getAllDisplayedColumns();
    if (columns.length === 0 || viewportWidth <= 0) return;
    const flexes = columns.map((column) => column.getColDef().flex ?? 1);
    const total = flexes.reduce((sum, flex) => sum + flex, 0);
    const widths = flexes.map((flex) => Math.floor((viewportWidth * flex) / total));
    widths[widths.length - 1] += viewportWidth - widths.reduce((sum, width) => sum + width, 0);
    const changes = columns
        .map((column, index) => ({ key: column, newWidth: widths[index] }))
        .filter(({ key, newWidth }) => key.getActualWidth() !== newWidth);
    const drift = columns.reduce((sum, column, index) => sum + Math.abs(column.getActualWidth() - widths[index]), 0);
    if (changes.length > 0 && drift <= columns.length * 2) api.setColumnWidths(changes);
}

function onGridReady({ api }: GridReadyEvent<KpiRow>) {
    let width = 0;
    const fill = () => fillRoundingGap(api, width);
    api.addEventListener('gridSizeChanged', (event: GridSizeChangedEvent<KpiRow>) => { width = event.clientWidth; fill(); });
    api.addEventListener('displayedColumnsWidthChanged', () => setTimeout(fill));
}

function linkTarget(key: string) {
    return key === 'contract' || key === 'name' ? '/size2mix/prototype/ref-production-fuels' : '/size2mix/prototype/ref-network-production';
}

function KpiTable({ section, rows, onEdit }: {
    section: SectionId;
    rows: KpiRow[];
    onEdit: (edit: KpiEdit) => void;
}) {
    const header = useMemo<ColDef<KpiRow>[]>(() => COLUMNS[section].map((column) => ({
        field: column.key,
        headerName: column.label,
        flex: column.flex ?? 1,
        editable: column.editable ?? false,
        cellClass: column.editable ? 'production-synthesis-editable' : 'production-synthesis-readonly',
        valueFormatter: ({ value }) => value === null || value === undefined ? '-' : String(value),
        cellRenderer: ({ value, valueFormatted }: ICellRendererParams<KpiRow>) => {
            if (column.link) {
                // Nemo LinksCellRender: contextual link to the entity, plain "-" when empty.
                if (value === null || value === undefined || value === '') return <span className="production-synthesis-inline">-</span>;
                return <Link to={linkTarget(column.key)} className="nj-link nj-link--contextual production-synthesis-link production-synthesis-inline">{String(value)}</Link>;
            }
            const text = valueFormatted ?? (value === null || value === undefined ? '-' : String(value));
            const multiline = section === 'pumps' && (column.key === 'flow' || column.key === 'efficiency');
            return (
                <NJTooltip text={text} hasArrow isColorInverse={false} placement="top">
                    <span className={`production-synthesis-cell-tooltip${multiline ? ' production-synthesis-cell-tooltip-multiline' : ''}`}>{text}</span>
                </NJTooltip>
            );
        },
    })), [section]);

    const changed = (event: CellValueChangedEvent<KpiRow>) => {
        const field = event.colDef.field;
        if (!field || !event.data || event.newValue === event.oldValue) return;
        onEdit({ section, id: event.data.id, field, value: event.newValue === '' ? null : event.newValue });
    };

    return (
        <div className={`production-synthesis-table${rows.length === 0 ? ' production-synthesis-table-empty' : ''}`}>
            <DataTable<KpiRow>
                testId={`production-synthesis-${section}`}
                header={header}
                data={rows}
                defaultColDef={{ flex: 1, minWidth: 40, resizable: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true }}
                onCellValueChanged={changed}
                onGridReady={onGridReady}
            />
        </div>
    );
}

export default function SynthesisTab({ data }: { data: SynthesisData }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTION_IDS);
    const edit = useEditSynthesis();

    return (
        <div className="production-synthesis">
            {edit.isError && <NJInlineMessage variant="error" title="Modification impossible">{edit.error.message}</NJInlineMessage>}
            <Size2MixAccordion>
                <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
                {SECTION_IDS.map((section) => (
                    <Size2MixSection key={section} title={TITLES[section]} {...bind(section)}>
                        <KpiTable section={section} rows={data[section]} onEdit={(change) => edit.mutate(change)} />
                    </Size2MixSection>
                ))}
            </Size2MixAccordion>
        </div>
    );
}
